import React, { useState } from 'react';
import { 
  Target, 
  Calendar, 
  TrendingUp, 
  AlertCircle,
  Search,
  Filter,
  Plus,
  Edit2,
  Trash2,
  Eye,
  Download,
  CheckCircle,
  Clock,
  MapPin,
  BarChart3,
  Activity,
  FileText
} from 'lucide-react';

const DAP = () => {
  const [activeTab, setActiveTab] = useState('plans');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [plans, setPlans] = useState([
    { id: 1, district: 'Chennai', year: '2024-25', budget: '200,00,00,000', allocated: '180,00,00,000', status: 'approved', progress: 45, activities: 12, startDate: '2024-04-01' },
    { id: 2, district: 'Coimbatore', year: '2024-25', budget: '150,00,00,000', allocated: '120,00,00,000', status: 'in-review', progress: 30, activities: 10, startDate: '2024-05-01' },
    { id: 3, district: 'Madurai', year: '2024-25', budget: '180,00,00,000', allocated: '170,00,00,000', status: 'approved', progress: 60, activities: 15, startDate: '2024-03-15' },
    { id: 4, district: 'Tiruchirappalli', year: '2024-25', budget: '120,00,00,000', allocated: '100,00,00,000', status: 'approved', progress: 35, activities: 8, startDate: '2024-04-20' },
  ]);

  const [activities, setActivities] = useState([
    { id: 1, activity: 'Infrastructure Development', district: 'Chennai', department: 'Public Works', startDate: '2024-04-01', endDate: '2024-12-31', status: 'ongoing', priority: 'high', budget: '50,00,00,000', completion: 55 },
    { id: 2, activity: 'Healthcare Expansion', district: 'Coimbatore', department: 'Health', startDate: '2024-05-15', endDate: '2025-01-15', status: 'planned', priority: 'medium', budget: '30,00,00,000', completion: 10 },
    { id: 3, activity: 'Education Program', district: 'Madurai', department: 'Education', startDate: '2024-03-10', endDate: '2024-11-30', status: 'completed', priority: 'high', budget: '40,00,00,000', completion: 100 },
    { id: 4, activity: 'Water Supply Project', district: 'Tiruchirappalli', department: 'Water Resources', startDate: '2024-04-20', endDate: '2024-12-20', status: 'ongoing', priority: 'critical', budget: '35,00,00,000', completion: 40 },
  ]);

  const stats = {
    totalPlans: plans.length,
    approvedPlans: plans.filter(p => p.status === 'approved').length,
    totalBudget: plans.reduce((sum, p) => sum + parseFloat(p.budget.replace(/,/g, '')), 0),
    avgProgress: Math.round(plans.reduce((sum, p) => sum + p.progress, 0) / plans.length),
    ongoingActivities: activities.filter(a => a.status === 'ongoing').length,
    completedActivities: activities.filter(a => a.status === 'completed').length,
  };

  const filteredPlans = plans.filter(plan => {
    const matchesSearch = plan.district.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || plan.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const filteredActivities = activities.filter(activity => {
    const matchesSearch = activity.activity.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          activity.district.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          activity.department.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || activity.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'critical': return 'bg-red-100 text-red-700';
      case 'high': return 'bg-orange-100 text-orange-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'completed':
      case 'approved':
        return <CheckCircle className="w-4 h-4" />;
      case 'ongoing':
      case 'in-review':
      case 'planned':
        return <Clock className="w-4 h-4" />;
      case 'delayed':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">District Action Plan (DAP)</h1>
          <p className="text-gray-600 mt-1">Monitor and manage district-level action plans</p>
        </div>
        <button className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#3d82c4] hover:bg-[#2d6fad] text-white rounded-lg font-medium transition-colors shadow-sm">
          <Plus className="w-5 h-5" />
          {activeTab === 'plans' ? 'Create Plan' : activeTab === 'activities' ? 'Add Activity' : 'New Report'}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Total Plans</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">{stats.totalPlans}</p>
              <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                <span>{stats.approvedPlans} approved</span>
              </p>
            </div>
            <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center">
              <Target className="w-7 h-7 text-[#3d82c4]" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Total Budget</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">₹{(stats.totalBudget / 10000000).toFixed(0)}Cr</p>
              <p className="text-xs text-gray-500 mt-2">Allocated funds</p>
            </div>
            <div className="w-14 h-14 bg-green-50 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-7 h-7 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Active Activities</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">{stats.ongoingActivities}</p>
              <p className="text-xs text-gray-500 mt-2">{stats.completedActivities} completed</p>
            </div>
            <div className="w-14 h-14 bg-purple-50 rounded-xl flex items-center justify-center">
              <Activity className="w-7 h-7 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Avg Progress</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">{stats.avgProgress}%</p>
              <p className="text-xs text-gray-500 mt-2">Overall completion</p>
            </div>
            <div className="w-14 h-14 bg-orange-50 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-7 h-7 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-lg border border-gray-200">
        {/* Tabs */}
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex gap-6">
            {['plans', 'activities', 'monitoring'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`
                  pb-3 px-1 font-medium text-sm border-b-2 transition-colors capitalize
                  ${activeTab === tab
                    ? 'border-[#3d82c4] text-[#3d82c4]'
                    : 'border-transparent text-gray-600 hover:text-gray-800'
                  }
                `}
              >
                {tab === 'plans' ? 'Action Plans' : tab === 'activities' ? 'Activities' : 'Monitoring'}
              </button>
            ))}
          </div>
        </div>

        {/* Search & Filter Bar */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={`Search ${activeTab}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d82c4] focus:border-transparent"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d82c4] focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="approved">Approved</option>
                <option value="in-review">In Review</option>
                <option value="ongoing">Ongoing</option>
                <option value="completed">Completed</option>
                <option value="planned">Planned</option>
              </select>
              <button className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
                <Download className="w-5 h-5" />
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'plans' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">District</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Year</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Budget</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Progress</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Activities</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPlans.map(plan => (
                    <tr key={plan.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-[#3d82c4]" />
                          <span className="font-semibold text-gray-800">{plan.district}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-700">{plan.year}</td>
                      <td className="py-4 px-4">
                        <div>
                          <p className="font-semibold text-gray-800">₹{(parseFloat(plan.budget.replace(/,/g, '')) / 10000000).toFixed(0)}Cr</p>
                          <p className="text-xs text-gray-500">Allocated: ₹{(parseFloat(plan.allocated.replace(/,/g, '')) / 10000000).toFixed(0)}Cr</p>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden min-w-[80px]">
                            <div 
                              className="h-full bg-gradient-to-r from-[#3d82c4] to-[#2d6fad] rounded-full"
                              style={{ width: `${plan.progress}%` }}
                            />
                          </div>
                          <span className="text-sm font-semibold text-gray-700 min-w-[40px]">{plan.progress}%</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="inline-flex px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                          {plan.activities} activities
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`
                          inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium capitalize
                          ${plan.status === 'approved' ? 'bg-green-100 text-green-700' :
                            plan.status === 'in-review' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }
                        `}>
                          {getStatusIcon(plan.status)}
                          {plan.status}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'activities' && (
            <div className="space-y-4">
              {filteredActivities.map(activity => (
                <div key={activity.id} className="border border-gray-200 rounded-lg p-5 hover:border-[#3d82c4] transition-all">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex gap-4 flex-1">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#3d82c4] to-[#2d6fad] rounded-xl flex items-center justify-center flex-shrink-0">
                        <Activity className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-800">{activity.activity}</h3>
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${getPriorityColor(activity.priority)}`}>
                            {activity.priority}
                          </span>
                          <span className={`
                            inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium capitalize
                            ${activity.status === 'completed' ? 'bg-green-100 text-green-700' :
                              activity.status === 'ongoing' ? 'bg-blue-100 text-blue-700' :
                              'bg-yellow-100 text-yellow-700'
                            }
                          `}>
                            {getStatusIcon(activity.status)}
                            {activity.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 mb-3">
                          <div>
                            <p className="text-xs text-gray-500">District</p>
                            <p className="font-medium text-gray-800">{activity.district}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Department</p>
                            <p className="font-medium text-gray-800">{activity.department}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Budget</p>
                            <p className="font-medium text-gray-800">₹{(parseFloat(activity.budget.replace(/,/g, '')) / 10000000).toFixed(1)}Cr</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Timeline</p>
                            <p className="font-medium text-gray-800">{activity.startDate} - {activity.endDate}</p>
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-gray-600 font-medium">Completion</span>
                            <span className="font-semibold text-gray-800">{activity.completion}%</span>
                          </div>
                          <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-[#3d82c4] to-[#2d6fad] rounded-full transition-all"
                              style={{ width: `${activity.completion}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <Eye className="w-5 h-5" />
                      </button>
                      <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                        <Edit2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'monitoring' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* District-wise Progress */}
                <div className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-[#3d82c4]" />
                    District-wise Progress
                  </h3>
                  <div className="space-y-4">
                    {plans.map(plan => (
                      <div key={plan.id}>
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-gray-700 font-medium">{plan.district}</span>
                          <span className="text-gray-600">{plan.progress}%</span>
                        </div>
                        <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-[#3d82c4] to-[#2d6fad] rounded-full"
                            style={{ width: `${plan.progress}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Budget Utilization */}
                <div className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-green-600" />
                    Budget Utilization
                  </h3>
                  <div className="space-y-4">
                    {plans.map(plan => {
                      const utilization = Math.round((parseFloat(plan.allocated.replace(/,/g, '')) / parseFloat(plan.budget.replace(/,/g, ''))) * 100);
                      return (
                        <div key={plan.id}>
                          <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-gray-700 font-medium">{plan.district}</span>
                            <span className="text-gray-600">{utilization}%</span>
                          </div>
                          <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full"
                              style={{ width: `${utilization}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Performance Summary */}
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-[#3d82c4]" />
                  Performance Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <Calendar className="w-8 h-8 text-[#3d82c4] mx-auto mb-2" />
                    <p className="text-2xl font-bold text-gray-800">{plans.length}</p>
                    <p className="text-sm text-gray-600 mt-1">Active Plans</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-gray-800">{stats.completedActivities}</p>
                    <p className="text-sm text-gray-600 mt-1">Completed Activities</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 text-center">
                    <Activity className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-gray-800">{stats.ongoingActivities}</p>
                    <p className="text-sm text-gray-600 mt-1">Ongoing Activities</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4 text-center">
                    <TrendingUp className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-gray-800">{stats.avgProgress}%</p>
                    <p className="text-sm text-gray-600 mt-1">Average Progress</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DAP;